import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Cookie } from "lucide-react";

interface CookiePreferences {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
}

export const CookieBanner = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    functional: false,
    analytics: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem("cookie-consent");
    if (!consent) {
      setShowBanner(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
    };
    saveConsent(allAccepted);
    setShowBanner(false);
  };

  const handleReject = () => {
    const rejected = {
      necessary: true,
      functional: false,
      analytics: false,
    };
    saveConsent(rejected);
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
    setShowPreferences(false);
    setShowBanner(false);
  };

  const saveConsent = (prefs: CookiePreferences) => {
    localStorage.setItem("cookie-consent", JSON.stringify(prefs));
    localStorage.setItem("cookie-consent-date", new Date().toISOString());
    
    // Gestione Google Analytics
    if (prefs.analytics) {
      enableGoogleAnalytics();
    } else {
      disableGoogleAnalytics();
    }
  };

  const enableGoogleAnalytics = () => {
    // @ts-ignore
    if (window.gtag) {
      // @ts-ignore
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
  };

  const disableGoogleAnalytics = () => {
    // @ts-ignore
    if (window.gtag) {
      // @ts-ignore
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }
  };

  return (
    <>
      <Dialog open={showBanner} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-lg" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cookie className="w-5 h-5 text-primary" />
              {t("cookieBanner.title")}
            </DialogTitle>
            <DialogDescription>
              {t("cookieBanner.description")}{" "}
              <a 
                href="/cookie-policy" 
                className="text-primary hover:underline"
              >
                Cookie Policy
              </a>
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              onClick={handleReject}
              variant="outline"
              className="w-full sm:w-auto"
            >
              {t("cookieBanner.reject")}
            </Button>
            <Button
              onClick={() => setShowPreferences(true)}
              variant="outline"
              className="w-full sm:w-auto"
            >
              {t("cookieBanner.managePreferences")}
            </Button>
            <Button
              onClick={handleAcceptAll}
              variant="default"
              className="w-full sm:w-auto"
            >
              {t("cookieBanner.acceptAll")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showPreferences} onOpenChange={setShowPreferences}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cookie className="w-5 h-5 text-primary" />
              {t("cookieBanner.preferencesTitle")}
            </DialogTitle>
            <DialogDescription>
              {t("cookieBanner.preferencesDescription")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="flex items-start space-x-3 p-4 bg-muted/50 rounded-lg border border-border">
              <Checkbox 
                id="necessary" 
                checked={preferences.necessary}
                disabled
                className="mt-1"
              />
              <div className="flex-1">
                <label
                  htmlFor="necessary"
                  className="text-sm font-semibold leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {t("cookieBanner.necessaryTitle")}
                </label>
                <p className="text-sm text-muted-foreground mt-2">
                  {t("cookieBanner.necessaryDescription")}
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3 p-4 bg-muted/50 rounded-lg border border-border">
              <Checkbox 
                id="functional" 
                checked={preferences.functional}
                onCheckedChange={(checked) => 
                  setPreferences({ ...preferences, functional: checked as boolean })
                }
                className="mt-1"
              />
              <div className="flex-1">
                <label
                  htmlFor="functional"
                  className="text-sm font-semibold leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  {t("cookieBanner.functionalTitle")}
                </label>
                <p className="text-sm text-muted-foreground mt-2">
                  {t("cookieBanner.functionalDescription")}
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3 p-4 bg-muted/50 rounded-lg border border-border">
              <Checkbox 
                id="analytics" 
                checked={preferences.analytics}
                onCheckedChange={(checked) => 
                  setPreferences({ ...preferences, analytics: checked as boolean })
                }
                className="mt-1"
              />
              <div className="flex-1">
                <label
                  htmlFor="analytics"
                  className="text-sm font-semibold leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  {t("cookieBanner.analyticsTitle")}
                </label>
                <p className="text-sm text-muted-foreground mt-2">
                  {t("cookieBanner.analyticsDescription")}
                </p>
              </div>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              onClick={() => setShowPreferences(false)}
              variant="outline"
              className="w-full sm:w-auto"
            >
              {t("cookieBanner.cancel")}
            </Button>
            <Button
              onClick={handleSavePreferences}
              className="w-full sm:w-auto"
            >
              {t("cookieBanner.savePreferences")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
